import JSZip from "jszip";
import Papa from "papaparse";
import proj4 from "proj4";
import { useState } from "react";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import FileDrop from "senf-atomic-design-system/src/components/core/fileDrop/FileDrop";
import * as shapefile from "shapefile";

// Define the UTM projection string for your specific zone and the WGS84 projection string
const utmProjection =
  "+proj=utm +zone=32 +ellps=WGS84 +datum=WGS84 +units=m +no_defs"; // Example: Zone 32N
const wgs84Projection = "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs";

// Helper function to check if a value is a finite number
const isFiniteNumber = (value) => {
  return typeof value === "number" && isFinite(value);
};

// Function to convert a single coordinate
const convertCoordinate = (coord, fromProjection, toProjection) => {
  if (isFiniteNumber(coord[0]) && isFiniteNumber(coord[1])) {
    return proj4(fromProjection, toProjection, coord);
  }
  console.error("Invalid coordinate:", coord);
  return null; // Or handle invalid coordinates as needed
};

// Function to convert coordinates in a GeoJSON object
const convertGeoJSONCoordinates = (geojson, fromProjection, toProjection) => {
  geojson.features = geojson.features.map((feature) => {
    const { coordinates } = feature.geometry;
    if (feature.geometry.type === "Point") {
      const convertedCoord = convertCoordinate(
        coordinates,
        fromProjection,
        toProjection
      );
      if (convertedCoord) {
        feature.geometry.coordinates = convertedCoord;
      }
    } else if (
      feature.geometry.type === "MultiPoint" ||
      feature.geometry.type === "LineString"
    ) {
      feature.geometry.coordinates = coordinates
        .map((coord) => convertCoordinate(coord, fromProjection, toProjection))
        .filter((coord) => coord !== null);
    } else if (
      feature.geometry.type === "MultiLineString" ||
      feature.geometry.type === "Polygon"
    ) {
      feature.geometry.coordinates = coordinates
        .map((line) =>
          line.map((coord) =>
            convertCoordinate(coord, fromProjection, toProjection)
          )
        )
        .map((line) => line.filter((coord) => coord !== null));
    } else if (feature.geometry.type === "MultiPolygon") {
      feature.geometry.coordinates = coordinates.map((polygon) =>
        polygon
          .map((line) =>
            line.map((coord) =>
              convertCoordinate(coord, fromProjection, toProjection)
            )
          )
          .map((line) => line.filter((coord) => coord !== null))
      );
    }
    return feature;
  });
  return geojson;
};

// Function to check if the coordinates seem to be in UTM format (simple heuristic)
const seemsUTM = (coord) => {
  // Check if the coordinate is likely in UTM format by its numeric range
  return Math.abs(coord[0]) > 180 || Math.abs(coord[1]) > 90;
};

// Function to find the first valid coordinate in a potentially complex GeoJSON structure
const findFirstValidCoordinate = (coordinates) => {
  let firstCoord = coordinates;
  while (Array.isArray(firstCoord[0])) {
    firstCoord = firstCoord[0];
    if (firstCoord.length === 0) {
      return null; // No valid coordinate found
    }
  }
  return firstCoord;
};

// Function to convert a GeoJSON object from UTM to WGS84 if necessary
const autoConvertGeoJSONFromUTMToWGS84 = (geojson, utmProjection) => {
  if (
    geojson.features.length > 0 &&
    geojson.features[0].geometry.coordinates.length > 0
  ) {
    // Find the first valid coordinate to check
    const firstFoundCoordinate = findFirstValidCoordinate(
      geojson.features[0].geometry.coordinates
    );

    // If the first coordinate doesn't seem to be in UTM or is null, return the original geojson
    if (!firstFoundCoordinate || !seemsUTM(firstFoundCoordinate)) {
      return geojson;
    }
  }

  // Proceed with conversion if the first coordinate seems to be in UTM
  return convertGeoJSONCoordinates(geojson, utmProjection, wgs84Projection);
};

const GeoJSONUploader = ({ onFileUpload, text, fileTypes, fileTypesText, isButton = false, ...props }) => {
  const [file, setFile] = useState(null);

  const extractFromZip = async (file) => {
    try {
      const zip = await JSZip.loadAsync(file);

      // Filter out the .shp and .dbf files, ignoring macOS metadata files
      const shpFiles = zip.file(/^(?!__MACOSX\/).*\.shp$/i); // Ignore macOS metadata files
      const dbfFiles = zip.file(/^(?!__MACOSX\/).*\.dbf$/i); // Ignore macOS metadata files

      const pairs = [];

      // Map dbf files by name for quick access
      const dbfMap = dbfFiles.reduce((map, file) => {
        map[file.name.replace(".dbf", "")] = file;
        return map;
      }, {});

      // Iterate over shpFiles and find the corresponding dbf file
      shpFiles.forEach((shpFile) => {
        const baseName = shpFile.name.replace(".shp", "");
        if (dbfMap[baseName]) {
          pairs.push({ shpFile, dbfFile: dbfMap[baseName] });
        }
      });

      // Error handling if no pairs are found
      if (pairs.length === 0) {
        alert("No matching .shp and .dbf file pairs found.");
        return null;
      }

      return pairs;
    } catch (error) {
      console.error("Error extracting files from zip:", error);
      alert("Error extracting files from zip.");
      return null; // Return null to indicate an error
    }
  };

  const shpToGeoJSON = async (fileContent) => {
    const pairs = await extractFromZip(fileContent);

    if (!pairs || pairs.length === 0) {
      return; // Exit early if there's an error
    }

    try {
      // Assuming pairs is an array, we can use a traditional for loop to get access to the index
      for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];
        const shpArrayBuffer = await pair.shpFile.async("arraybuffer");
        const dbfArrayBuffer = await pair.dbfFile.async("arraybuffer");

        const geojson = await shapefile.read(shpArrayBuffer, dbfArrayBuffer);

        const convertedGeoJSON = autoConvertGeoJSONFromUTMToWGS84(
          geojson,
          utmProjection
        );

        // Now you can use the index `i` if needed
        onFileUpload(
          { data: convertedGeoJSON, title: pair.shpFile.name }, // Example usage of index
          "zip",
          i
        );
      }
    } catch (error) {
      console.error(error);
      // Handle any errors
    }
  };

  const csvToGeoJSON = (csvData) => {
    const geojson = {
      type: "FeatureCollection",
      features: [],
    };

    Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        results.data.forEach((row) => {
          const long =
            row?.Longitude ||
            row?.longitude ||
            row?.Long ||
            row?.long ||
            row?.Lon ||
            row?.lon;
          const lat = row?.Latitude || row?.latitude || row?.Lat || row.lat;

          const formattedLong = Number.parseFloat(long?.replace(",", "."));
          const formattedLat = Number.parseFloat(lat?.replace(",", "."));

          const feature = {
            type: "Feature",
            properties: row,
            geometry: {
              type: "Point",
              coordinates: [formattedLong, formattedLat],
            },
          };
          geojson.features.push(feature);
        });
      },
    });

    return geojson;
  };

  const convertToGeoJSON = async (fileContent, fileType) => {
    if (fileType === "geojson" || fileType === "json") {
      const geojson = JSON.parse(fileContent);
      const convertedGeoJSON = autoConvertGeoJSONFromUTMToWGS84(
        geojson,
        utmProjection
      );

      return await convertedGeoJSON;
    }

    if (fileType === "csv") {
      return csvToGeoJSON(fileContent);
    }

    if (fileType === "zip" || fileType === "shp") {
      return shpToGeoJSON(fileContent);
    }

    throw new Error("Unsupported file format");
  };

  const handleFileChange = async (files) => {
    const selectedFile = files[0];
    setFile(selectedFile);

    if (selectedFile) {
      const fileType = selectedFile.name.split(".").pop();

      if (fileType === "geojson" || fileType === "json") {
        const reader = new FileReader();
        reader.onload = async () => {
          const geojson = await convertToGeoJSON(reader.result, fileType);
          setTimeout(() => {
            onFileUpload({ data: geojson, title: selectedFile.name }, fileType);
          }, 1000);
        };
        reader.readAsText(selectedFile);
      } else if (fileType === "zip") {
        await convertToGeoJSON(selectedFile, fileType);
      } else {
        try {
          const geojson = await convertToGeoJSON(selectedFile, fileType);
          setTimeout(() => {
            onFileUpload({ data: geojson, title: selectedFile.name }, fileType);
          }, 1000);
        } catch (error) {
          alert(`Error processing the file: ${error.message}`);
        }
      }
    }
  };

  return (
    <div>
      {isButton ? (
        <Button
          text={text}
          leadingIcon="upload"
          onClick={() => {
            // Programmatically trigger file input click
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = fileTypes.join(',');
            input.onchange = (e) => {
              const target = e.target as HTMLInputElement;
              if (target.files) {
                handleFileChange(target.files);
              }
            };
            input.click();
          }}
          {...props}
        />
      ) : (
        <FileDrop
          onDrop={handleFileChange}
          showImage={false}
          multiple={true}
          text={text}
          icon="file/json-28"
          fileTypes={fileTypes}
          fileTypesText={fileTypesText}
        />
      )}
    </div>
  );
};

export default GeoJSONUploader;
